import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ListChecks } from 'lucide-react'
import { catalog as api } from '@/api/endpoints'
import { Err } from '@/components/Err'
import { Facts } from '@/components/Facts'
import { InlineEdit } from '@/components/InlineEdit'
import { ToneBadge } from '@/components/ToneBadge'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { coresText, diskText, gpuText, memoryText } from '@/domain/flavor'
import { usePoll } from '@/hooks/usePoll'
import { ACTION, ACTION_DANGER, CHIP } from './vm/chips'
import { FlavorDeleteForm } from './FlavorForms'

const POLL_MS = 30000

// 사양 상세(F-105) — 카탈로그의 줄을 누르면 온다. 사양 하나를 읽는 엔드포인트는 없다: 목록에서 찾는다.
// 자원은 만들 때 정해져 여기서 고치지 않는다. 고치는 것은 표시 이름(제목을 눌러 그 자리에서)과 신청 가능 여부이고, 삭제는
// 그 사양으로 만든 VM 과 무관하다(F-107).

type Open = 'remove' | null

export default function FlavorView() {
  const { id } = useParams()
  // 경로의 사양이 바뀌면 처음부터 그린다 — 앞 사양의 오류·열린 창·고치던 값이 다음 사양에 남지 않는다.
  return <FlavorDetail key={id} id={id} />
}

function FlavorDetail({ id }: { id: string | undefined }) {
  const nav = useNavigate()
  const flavors = usePoll(() => api.flavors(), POLL_MS)
  const [open, setOpen] = useState<Open>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const flavor = (flavors.data ?? []).find((f) => f.id === id)

  if (!flavor) return flavors.loading ? <Empty>불러오는 중</Empty> : <Empty>없는 사양</Empty>

  const close = () => setOpen(null)
  const toggle = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.patchFlavor(flavor.id, { enabled: !flavor.enabled })
      flavors.refresh()
    } catch (err) {
      setError(err)
    }
    setBusy(false)
  }

  return (
    <>
      <Crumb steps={[{ label: '카탈로그', to: '/catalog' }, { label: flavor.display_name }]} />
      <PageHead
        icon={<ListChecks className="size-5" />}
        kind="사양"
        title={
          <InlineEdit
            label="이름"
            value={flavor.display_name}
            display={flavor.display_name}
            required
            onSave={async (v) => {
              await api.patchFlavor(flavor.id, { display_name: v })
              flavors.refresh()
            }}
          />
        }
        afterTitle={<ToneBadge tone={flavor.enabled ? 'ok' : 'none'}>{flavor.enabled ? '신청 가능' : '신청 불가'}</ToneBadge>}
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
            <button type="button" className={CHIP} disabled={busy} onClick={() => void toggle()}>
              {flavor.enabled ? '신청 불가' : '신청 가능'}
            </button>
            <button type="button" className={`${ACTION} ${ACTION_DANGER}`} onClick={() => setOpen('remove')}>
              삭제
            </button>
          </div>
        }
      />

      <Err error={flavors.error} />
      <Err error={error} />

      <Section title="자원">
        {/* 자유로 둔 자원은 값이 없는 것이 아니라 신청하는 사람이 고르는 것이다(F-62). */}
        <Facts
          rows={[
            ['코어', coresText(flavor)],
            ['메모리', memoryText(flavor)],
            ['디스크', diskText(flavor)],
            ['GPU', gpuText(flavor)],
          ]}
          expert={[['이름', <span className="font-mono">{flavor.name}</span>]]}
        />
      </Section>

      <Dialog open={open !== null} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined}>
          {open === 'remove' ? <FlavorDeleteForm flavors={[flavor]} onClose={close} onDone={() => nav('/catalog')} onRefused={flavors.refresh} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
